import { useMemo, useState } from 'react';
import { Kabelverteilschrank, Abgangsleitung } from '../../../types/network';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useSettingsStore } from '../../../store/settingsStore';
import { PowerMeasurementsSection } from './PowerMeasurementsSection';

import { BusbarsSection } from './kabelverteilschrank/components/BusbarsSection';
import { StationDiagramModal } from './trafostation/components/StationDiagramModal';
import { detailStyles, headerColors } from '../../../styles/detailStyles';

interface KabelverteilschrankDetailsProps {
  element: Kabelverteilschrank;
}

export const KabelverteilschrankDetails = ({ element: initialElement }: KabelverteilschrankDetailsProps) => {
  const { elements, segments, loadElements } = useNetworkElementsStore();
  const thresholds = useSettingsStore(state => state.thresholds.cableLoadLevels);
  const [diagramOpen, setDiagramOpen] = useState(false);

  const element = elements.find(el => el.id === initialElement.id) as Kabelverteilschrank;

  // Find connected cables using busIndex from cable_segments (from_bus/to_bus)
  const allCables = useMemo(() =>
    elements.filter((el): el is Abgangsleitung => el.type === 'abgangsleitung'),
    [elements]
  );

  if (!element) {
    console.error('[KabelverteilschrankDetails] Element not found in store:', initialElement.id);
    return null;
  }

  return (
    <div className={detailStyles.layout.section}>
      {/* Cyan Header */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.kabelverteilschrank.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.kabelverteilschrank.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Kabelverteilschrank</h2>
              <p className={detailStyles.header.subtitle}>{element.name}</p>
            </div>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.kabelverteilschrank.id}`}>ID: {element.id}</div>
      </div>

      <PowerMeasurementsSection
        elementId={element.id}
        isGridInfrastructure={true}
        hidePhaseValues={true}
        hideDetailedMeasurements={true}
      />

      <button
        onClick={() => setDiagramOpen(true)}
        className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
        title="Übersichtsschaltplan des Schranks: Sammelschienen, Kupplungen, Abgänge mit Sicherung, Kabel und Ziel"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5h16M12 5v6m-6 0h12M6 11v8m6-8v8m6-8v8" />
        </svg>
        Schrankansicht
      </button>

      <StationDiagramModal
        isOpen={diagramOpen}
        element={element}
        onClose={() => setDiagramOpen(false)}
      />

      {/* Multi-Busbar Management Section */}
      <BusbarsSection
        kvs={element}
        allCables={allCables}
        segments={segments}
        thresholds={thresholds}
        onUpdate={() => loadElements()}
      />

    </div>
  );
};
